import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CollectionContext } from "../Contexts/CollectionContext";
import { CategoryContext } from "../Contexts/CategoryContext";
import Alert from "../components/Alert";
import Loading from '../pages/Loading';

const CollectionEditForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getCollection, updateCollection } = useContext(CollectionContext);
  const { category: categories, loading, error } = useContext(CategoryContext);

  const [name, setName] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [alert, setAlert] = useState({status: "", message: ""});

  useEffect(() => {
    const fetchCollection = async () => {
      try {
        const fetchedCollection = await getCollection(id);
        setName(fetchedCollection.name);
        setSelectedCategory(fetchedCollection.categories);
        setImagePreview(fetchedCollection.image ? `https://sawaapi.space/${fetchedCollection.image}` : "");
      } catch (error) {
        console.error("Error fetching collection:", error);
      }
    };

    fetchCollection();
  }, [id]);

  useEffect(() => {
    if (image) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(image);
    }
  }, [image]);

  const handleImageChange = (event) => {
    setImage(event.target.files[0]);
  };

  const handleCancel = () => {
    navigate("/admin/view/collections");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const formData = new FormData();
    formData.append("name", name);
    if (image) {
      formData.append("image", image);
    }
    if (selectedCategory) {
      formData.append("categories", selectedCategory);
    }

    try {
      await updateCollection(id, formData);
      // console.log("Collection updated successfully");
      setAlert({ status: "success", message: "Collection updated successfully!" });
      setTimeout(() => {
        navigate("/admin/view/collections");
      }, 2000);
    } catch (error) {
      const errorMessage = error.message || "Failed to update collection.";
      setAlert({ status: "error", message: errorMessage });
    }
  };

  if (loading) return <Loading />;
  if (error) return <p>Error loading categories: {error.message}</p>;

  return (
    <div className="bg-gray-100 py-10">
          {alert.message && (
            <Alert
              status={alert.status}
              message={alert.message}
              onClose={() => setAlert({ status: "", message: "" })}
            />
          )}
      <div className="max-w-3xl mx-auto bg-white p-8 rounded-lg shadow-lg">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">Update Collection Form</h1>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="name" className="block text-gray-700 font-medium mb-2">Collection Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              required
            />
          </div>

          <div className="mb-4">
            <label htmlFor="image" className="block text-gray-700 font-medium mb-2">Collection Image</label>
            {imagePreview && (
              <img
                src={imagePreview}
                alt="Preview"
                className="mb-4 w-40 h-40 object-cover rounded-lg"
              />
            )}
            <input
              type="file"
              id="image"
              name="image"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              onChange={handleImageChange}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="category" className="block text-gray-700 font-medium mb-2">Category</label>
            <select
              id="category"
              name="category"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              <option value="">Select a Category</option>
              {categories.map((category) => (
                <option key={category._id} value={category._id}>{category.name}</option>
              ))}
            </select>
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              Submit
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg shadow hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-600"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CollectionEditForm;
